import { getCountryCallingCode, CountryCode } from "libphonenumber-js";

export type Country = {
  /** ISO 3166-1 alpha-2 country code */
  code: CountryCode;
  /** Country calling code (e.g., "1" for US) */
  callingCode: string;
  /** Country name */
  name: string;
};

/**
 * Map of country codes to country names.
 * Only includes commonly used countries for phone numbers.
 */
const countryNames: Record<string, string> = {
  US: "United States",
  CA: "Canada",
  GB: "United Kingdom",
  AU: "Australia",
  DE: "Germany",
  FR: "France",
  IT: "Italy",
  ES: "Spain",
  NL: "Netherlands",
  BE: "Belgium",
  AT: "Austria",
  CH: "Switzerland",
  SE: "Sweden",
  NO: "Norway",
  DK: "Denmark",
  FI: "Finland",
  IE: "Ireland",
  PT: "Portugal",
  PL: "Poland",
  CZ: "Czech Republic",
  GR: "Greece",
  HU: "Hungary",
  RO: "Romania",
  BG: "Bulgaria",
  HR: "Croatia",
  SK: "Slovakia",
  SI: "Slovenia",
  EE: "Estonia",
  LV: "Latvia",
  LT: "Lithuania",
  LU: "Luxembourg",
  MT: "Malta",
  CY: "Cyprus",
  JP: "Japan",
  KR: "South Korea",
  CN: "China",
  HK: "Hong Kong",
  TW: "Taiwan",
  SG: "Singapore",
  MY: "Malaysia",
  TH: "Thailand",
  PH: "Philippines",
  ID: "Indonesia",
  VN: "Vietnam",
  IN: "India",
  PK: "Pakistan",
  BD: "Bangladesh",
  NP: "Nepal",
  LK: "Sri Lanka",
  AE: "United Arab Emirates",
  SA: "Saudi Arabia",
  QA: "Qatar",
  KW: "Kuwait",
  BH: "Bahrain",
  OM: "Oman",
  IL: "Israel",
  TR: "Turkey",
  EG: "Egypt",
  ZA: "South Africa",
  NG: "Nigeria",
  KE: "Kenya",
  GH: "Ghana",
  MA: "Morocco",
  TN: "Tunisia",
  MX: "Mexico",
  BR: "Brazil",
  AR: "Argentina",
  CL: "Chile",
  CO: "Colombia",
  PE: "Peru",
  VE: "Venezuela",
  EC: "Ecuador",
  UY: "Uruguay",
  PY: "Paraguay",
  BO: "Bolivia",
  CR: "Costa Rica",
  PA: "Panama",
  PR: "Puerto Rico",
  DO: "Dominican Republic",
  JM: "Jamaica",
  TT: "Trinidad and Tobago",
  NZ: "New Zealand",
  RU: "Russia",
  UA: "Ukraine",
  BY: "Belarus",
  KZ: "Kazakhstan",
  UZ: "Uzbekistan",
};

/**
 * Get the list of supported countries with their calling codes.
 * Sorted with US and CA first, then alphabetically.
 */
export function getCountryList(): Country[] {
  const supportedCountryCodes = Object.keys(countryNames) as CountryCode[];

  const countries = supportedCountryCodes
    .filter((code) => {
      try {
        // Verify the country code is valid in libphonenumber-js
        getCountryCallingCode(code);
        return true;
      } catch {
        return false;
      }
    })
    .map((code) => ({
      code,
      callingCode: getCountryCallingCode(code),
      name: countryNames[code] || code,
    }));

  // Sort: US first, then CA, then alphabetically by name
  return countries.sort((a, b) => {
    if (a.code === "US") return -1;
    if (b.code === "US") return 1;
    if (a.code === "CA") return -1;
    if (b.code === "CA") return 1;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Find a country by its ISO code.
 */
export function findCountryByCode(code: CountryCode | string): Country | undefined {
  const countries = getCountryList();
  return countries.find((c) => c.code === code);
}

/**
 * Find a country by its calling code.
 * Returns the first match (some countries share calling codes).
 */
export function findCountryByCallingCode(callingCode: string): Country | undefined {
  const countries = getCountryList();
  return countries.find((c) => c.callingCode === callingCode);
}

/**
 * Detect the default country code based on browser locale.
 * Falls back to US if detection fails.
 */
export function detectDefaultCountry(): CountryCode {
  if (typeof navigator === "undefined") {
    return "US";
  }

  // Try to get country from navigator.language (e.g., "en-US" -> "US")
  const locale = navigator.language || (navigator as any).userLanguage;
  if (locale) {
    const parts = locale.split("-");
    if (parts.length >= 2) {
      const countryCode = parts[1].toUpperCase() as CountryCode;
      if (findCountryByCode(countryCode)) {
        return countryCode;
      }
    }
  }

  // Try to get from navigator.languages
  const languages = navigator.languages || [];
  for (const lang of languages) {
    const parts = lang.split("-");
    if (parts.length >= 2) {
      const countryCode = parts[1].toUpperCase() as CountryCode;
      if (findCountryByCode(countryCode)) {
        return countryCode;
      }
    }
  }

  return "US";
}

// Pre-computed list for performance
let cachedCountryList: Country[] | null = null;

/**
 * Get the cached country list (computed once).
 */
export function getCachedCountryList(): Country[] {
  if (!cachedCountryList) {
    cachedCountryList = getCountryList();
  }
  return cachedCountryList;
}
